import React, { useEffect } from 'react';
import {
  Check,
  X,
  ArrowRight,
  Star,
  AlertTriangle
} from 'lucide-react';

const PricingComparison = () => {
  useEffect(() => {
    // Google Analytics
    window.dataLayer = window.dataLayer || [];
    function gtag(){window.dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', 'G-V8ESDMZ5NZ');

    // SEO-OPTIMIZED: Title and meta description
    document.title = 'TicketDesk vs Zendesk vs Freshdesk vs Crisp: Pricing Comparison 2025';

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Compare customer support software pricing. See why small businesses save $1000s/year with TicketDesk vs Zendesk, Freshdesk, Intercom & Crisp. Unlimited users included.';

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = 'https://www.ticketdesk.app/pricing-comparison';

    // Structured data for comparison
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "TicketDesk",
      "description": "Customer support software with unlimited users",
      "brand": {
        "@type": "Brand",
        "name": "TicketDesk"
      },
      "offers": {
        "@type": "AggregateOffer",
        "lowPrice": "0",
        "highPrice": "149",
        "priceCurrency": "USD",
        "offerCount": "4"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "ratingCount": "500"
      }
    };

    let script = document.querySelector('script[type="application/ld+json"]');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(structuredData);
  }, []);

  const trackEvent = (eventName, eventParams = {}) => {
    if (window.gtag) {
      window.gtag('event', eventName, eventParams);
    }
  };

  const handleStartFreeClick = (location) => {
    trackEvent('start_free_click', {
      button_location: location,
      page: 'pricing_comparison',
      value: 1
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="/" className="flex items-center min-w-0">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-message-circle">
                  <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719"></path>
                </svg>
              </div>
              <span className="ml-2 sm:ml-3 text-lg sm:text-xl font-semibold text-gray-900">TicketDesk</span>
            </a>
            <div className="flex items-center space-x-2 sm:space-x-4 lg:space-x-8">
              <a href="/#features" className="text-xs sm:text-sm text-gray-700 hover:text-gray-900 hidden md:block">Features</a>
              <a href="/#pricing" className="text-xs sm:text-sm text-gray-700 hover:text-gray-900">Pricing</a>
              <a href="/login" className="text-xs sm:text-sm text-gray-700 hover:text-gray-900 hidden lg:block">Sign In</a>
              <a href="/register" className="bg-purple-600 text-white px-3 sm:px-4 lg:px-5 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium hover:bg-purple-700 whitespace-nowrap">
                Try free
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
              Customer Support Software Pricing Comparison: Save Thousands with TicketDesk
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
              Enterprise software companies like Zendesk, Freshdesk, and Intercom charge per agent, making costs spiral as you grow. Small businesses end up paying thousands per year for basic features. TicketDesk offers unlimited team members at predictable flat-rate pricing—saving you $5,000 to $15,000 annually while giving you the same core features.
            </p>
          </div>
        </div>
      </section>

      {/* Simple Base Price Comparison */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing Comparison
            </h2>
            <p className="text-lg text-gray-600">
              Compare starting prices and what you actually get
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-12">
            {/* TicketDesk */}
            <div className="bg-purple-50 border-2 border-purple-500 rounded-xl p-6 relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full">BEST VALUE</span>
              </div>
              <div className="text-center mb-4">
                <div className="text-lg font-semibold text-gray-900 mb-2">TicketDesk</div>
                <div className="text-4xl font-bold text-purple-600 mb-1">$0</div>
                <div className="text-sm text-gray-600">Free Forever</div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>10 tickets/month</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span><strong>Unlimited users</strong></span>
                </div>
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Email ticketing</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Review monitoring</span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-purple-200">
                <div className="text-xs text-gray-700 font-semibold">Paid plans from $29/mo</div>
                <div className="text-xs text-gray-600">50-500 tickets/month</div>
              </div>
            </div>

            {/* Zendesk */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
              <div className="text-center mb-4">
                <div className="text-lg font-semibold text-gray-900 mb-2">Zendesk</div>
                <div className="text-4xl font-bold text-gray-900 mb-1">$19</div>
                <div className="text-sm text-gray-600">per agent/month</div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Email ticketing</span>
                </div>
                <div className="flex items-start">
                  <X className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Per-agent pricing</span>
                </div>
                <div className="flex items-start">
                  <X className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>No review monitoring</span>
                </div>
                <div className="flex items-start">
                  <X className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Limited features</span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="text-xs text-gray-700 font-semibold">Full features: $55-115/agent</div>
                <div className="text-xs text-gray-600">Costs add up fast</div>
              </div>
            </div>

            {/* Freshdesk */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
              <div className="text-center mb-4">
                <div className="text-lg font-semibold text-gray-900 mb-2">Freshdesk</div>
                <div className="text-4xl font-bold text-gray-900 mb-1">$15</div>
                <div className="text-sm text-gray-600">per agent/month</div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Email ticketing</span>
                </div>
                <div className="flex items-start">
                  <X className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Per-agent pricing</span>
                </div>
                <div className="flex items-start">
                  <X className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>No review monitoring</span>
                </div>
                <div className="flex items-start">
                  <X className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Basic features only</span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="text-xs text-gray-700 font-semibold">Pro: $49/agent</div>
                <div className="text-xs text-gray-600">Enterprise: $79/agent</div>
              </div>
            </div>

            {/* Intercom */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
              <div className="text-center mb-4">
                <div className="text-lg font-semibold text-gray-900 mb-2">Intercom</div>
                <div className="text-4xl font-bold text-gray-900 mb-1">$39</div>
                <div className="text-sm text-gray-600">per seat/month</div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Email + chat</span>
                </div>
                <div className="flex items-start">
                  <X className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Per-seat pricing</span>
                </div>
                <div className="flex items-start">
                  <X className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>No review monitoring</span>
                </div>
                <div className="flex items-start">
                  <span className="text-xs text-gray-600">+ $0.99 per resolution</span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="text-xs text-gray-700 font-semibold">Advanced: $99/seat</div>
                <div className="text-xs text-gray-600">Most expensive option</div>
              </div>
            </div>

            {/* Crisp */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
              <div className="text-center mb-4">
                <div className="text-lg font-semibold text-gray-900 mb-2">Crisp</div>
                <div className="text-4xl font-bold text-gray-900 mb-1">$25</div>
                <div className="text-sm text-gray-600">per agent/month</div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Live chat focus</span>
                </div>
                <div className="flex items-start">
                  <X className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Per-agent pricing</span>
                </div>
                <div className="flex items-start">
                  <X className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>No review monitoring</span>
                </div>
                <div className="flex items-start">
                  <X className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Limited ticketing</span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="text-xs text-gray-700 font-semibold">Unlimited: $95/workspace</div>
                <div className="text-xs text-gray-600">Still limited features</div>
              </div>
            </div>
          </div>

          <div className="bg-purple-100 rounded-xl p-6 text-center border-2 border-purple-500">
            <div className="text-2xl font-bold text-purple-900 mb-2">
              💡 TicketDesk Key Advantage: Unlimited Users on Every Plan
            </div>
            <div className="text-purple-700">Add your entire team without worrying about per-agent fees eating your budget</div>
          </div>
        </div>
      </section>

      {/* Why Per-Agent Pricing Hurts Small Teams */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <AlertTriangle className="w-16 h-16 text-orange-600 mx-auto mb-4" />
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Why Per-Agent Pricing Hurts Small Teams
            </h2>
          </div>

          <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
            <p>
              Enterprise software companies designed their pricing for Fortune 500 companies with dedicated support teams. When they charge $55-$78 per agent per month, they're assuming you have hundreds of employees and massive budgets.
            </p>

            <p>
              <strong>But small businesses operate differently.</strong> Your team wears multiple hats. Your sales person helps with support tickets. Your founder jumps in during busy periods. Your part-time assistant needs access to respond to basic questions.
            </p>

            <p>
              With per-agent pricing, <strong>every helpful team member costs you $55-$78 per month</strong>. Want to add your CEO so they can stay in the loop? That's $78/month. Need your bookkeeper to see billing-related tickets? Another $78/month. This pricing model forces you to artificially limit who can help customers—the opposite of good customer service.
            </p>

            <p>
              TicketDesk was built specifically for small businesses. <strong>Unlimited team members are included</strong> in every plan, starting at just $29/month. Add your entire company. Give everyone visibility. Let anyone who can help jump in. No penalties, no per-seat fees, no artificial restrictions.
            </p>

            <p className="text-purple-700 font-semibold text-xl">
              Stop paying for enterprise pricing when you're not an enterprise. Get the features you need at a price that makes sense.
            </p>
          </div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Feature-by-Feature Comparison
            </h2>
            <p className="text-lg text-gray-600">
              All the features you need at a fraction of the cost
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left p-4 font-semibold text-gray-900">Feature</th>
                  <th className="text-center p-4">
                    <div className="font-bold text-purple-600">TicketDesk</div>
                    <div className="text-sm text-gray-600 font-normal">From $29/mo</div>
                  </th>
                  <th className="text-center p-4">
                    <div className="font-bold text-gray-900">Zendesk</div>
                    <div className="text-sm text-gray-600 font-normal">From $55/agent</div>
                  </th>
                  <th className="text-center p-4">
                    <div className="font-bold text-gray-900">Freshdesk</div>
                    <div className="text-sm text-gray-600 font-normal">From $15/agent</div>
                  </th>
                  <th className="text-center p-4">
                    <div className="font-bold text-gray-900">Intercom</div>
                    <div className="text-sm text-gray-600 font-normal">From $78/agent</div>
                  </th>
                  <th className="text-center p-4">
                    <div className="font-bold text-gray-900">Crisp</div>
                    <div className="text-sm text-gray-600 font-normal">From $25/agent</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100 hover:bg-gray-50 bg-purple-50">
                  <td className="p-4 font-medium text-gray-900">Unlimited Team Members</td>
                  <td className="p-4 text-center">
                    <Check className="w-6 h-6 text-green-600 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <X className="w-6 h-6 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <X className="w-6 h-6 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <X className="w-6 h-6 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <X className="w-6 h-6 text-red-500 mx-auto" />
                  </td>
                </tr>

                <tr className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="p-4 font-medium text-gray-900">Email Ticketing (Gmail Integration)</td>
                  <td className="p-4 text-center">
                    <Check className="w-6 h-6 text-green-600 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <Check className="w-6 h-6 text-green-600 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <Check className="w-6 h-6 text-green-600 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <Check className="w-6 h-6 text-green-600 mx-auto" />
                  </td>
                </tr>

                <tr className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="p-4 font-medium text-gray-900">Facebook Messenger Support</td>
                  <td className="p-4 text-center">
                    <Check className="w-6 h-6 text-green-600 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <Check className="w-6 h-6 text-green-600 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <Check className="w-6 h-6 text-green-600 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <Check className="w-6 h-6 text-green-600 mx-auto" />
                  </td>
                </tr>

                <tr className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="p-4 font-medium text-gray-900">WhatsApp Business Integration</td>
                  <td className="p-4 text-center">
                    <Check className="w-6 h-6 text-green-600 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <Check className="w-6 h-6 text-green-600 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <Check className="w-6 h-6 text-green-600 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <div className="text-sm text-gray-500">Add-on</div>
                  </td>
                </tr>

                <tr className="border-b border-gray-100 hover:bg-gray-50 bg-purple-50">
                  <td className="p-4 font-medium text-gray-900">Review Monitoring (Trustpilot, G2, Capterra)</td>
                  <td className="p-4 text-center">
                    <Check className="w-6 h-6 text-green-600 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <X className="w-6 h-6 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <X className="w-6 h-6 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <X className="w-6 h-6 text-red-500 mx-auto" />
                  </td>
                </tr>

                <tr className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="p-4 font-medium text-gray-900">AI Response Polish</td>
                  <td className="p-4 text-center">
                    <Check className="w-6 h-6 text-green-600 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <div className="text-sm text-gray-500">Add-on</div>
                  </td>
                  <td className="p-4 text-center">
                    <div className="text-sm text-gray-500">Add-on</div>
                  </td>
                  <td className="p-4 text-center">
                    <Check className="w-6 h-6 text-green-600 mx-auto" />
                  </td>
                </tr>

                <tr className="border-b border-gray-100 hover:bg-gray-50 bg-purple-50">
                  <td className="p-4 font-medium text-gray-900">Setup Time</td>
                  <td className="p-4 text-center">
                    <div className="text-sm font-semibold text-green-600">60 seconds</div>
                  </td>
                  <td className="p-4 text-center">
                    <div className="text-sm text-gray-500">2-4 hours</div>
                  </td>
                  <td className="p-4 text-center">
                    <div className="text-sm text-gray-500">1-2 hours</div>
                  </td>
                  <td className="p-4 text-center">
                    <div className="text-sm text-gray-500">2-4 hours</div>
                  </td>
                </tr>

                <tr className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="p-4 font-medium text-gray-900">Free Plan Available</td>
                  <td className="p-4 text-center">
                    <div className="text-sm font-semibold text-green-600">Yes (10 tickets/mo)</div>
                  </td>
                  <td className="p-4 text-center">
                    <X className="w-6 h-6 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <div className="text-sm text-gray-500">Limited (10 agents)</div>
                  </td>
                  <td className="p-4 text-center">
                    <X className="w-6 h-6 text-red-500 mx-auto" />
                  </td>
                </tr>

                <tr className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="p-4 font-medium text-gray-900">Free Trial</td>
                  <td className="p-4 text-center">
                    <div className="text-sm font-semibold text-green-600">7 days</div>
                  </td>
                  <td className="p-4 text-center">
                    <div className="text-sm text-gray-500">14 days</div>
                  </td>
                  <td className="p-4 text-center">
                    <div className="text-sm text-gray-500">21 days</div>
                  </td>
                  <td className="p-4 text-center">
                    <div className="text-sm text-gray-500">14 days</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Customer Testimonial */}
      <section className="py-20 bg-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-2xl">
            <div className="flex items-center mb-6">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 text-yellow-400 fill-current" />
              ))}
            </div>
            <blockquote className="text-xl md:text-2xl text-gray-900 mb-6 italic">
              "We switched from Zendesk and saved $600/month while adding 3 more team members. The setup took literally 60 seconds with Gmail. Best decision we made for our customer support."
            </blockquote>
            <div className="flex items-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-purple-600 font-bold text-lg">SB</span>
              </div>
              <div>
                <div className="font-semibold text-gray-900">Sarah Bennett</div>
                <div className="text-gray-600 text-sm">Operations Manager, GrowthShop Co.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Stop Overpaying for Customer Support Software
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join hundreds of small businesses that switched from expensive per-agent pricing to TicketDesk
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <a
              href="/register"
              onClick={() => handleStartFreeClick('final_cta')}
              className="inline-flex items-center justify-center bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-purple-700"
            >
              Start Free Trial - No Credit Card Required
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
            <a
              href="/#pricing"
              className="inline-flex items-center justify-center border-2 border-purple-600 text-purple-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-purple-50"
            >
              View All Plans
            </a>
          </div>
          <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-600">
            <div className="flex items-center">
              <Check className="w-4 h-4 text-green-500 mr-2" />
              Free forever plan available
            </div>
            <div className="flex items-center">
              <Check className="w-4 h-4 text-green-500 mr-2" />
              Setup in 60 seconds
            </div>
            <div className="flex items-center">
              <Check className="w-4 h-4 text-green-500 mr-2" />
              Cancel anytime
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719"></path>
                  </svg>
                </div>
                <span className="ml-3 text-xl font-bold">TicketDesk</span>
              </div>
              <p className="text-gray-400 max-w-md">
                Affordable customer support software for small business. No per-agent pricing. Unlimited team members included.
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Product</h3>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><a href="/#features" className="hover:text-white">Features</a></li>
                <li><a href="/#pricing" className="hover:text-white">Pricing</a></li>
                <li><a href="/integrations" className="hover:text-white">Integrations</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Solutions</h3>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><a href="/for-ecommerce" className="hover:text-white">For E-commerce Shops</a></li>
                <li><a href="/for-saas" className="hover:text-white">For SaaS Companies</a></li>
                <li><a href="/for-agencies" className="hover:text-white">For Agencies</a></li>
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2"></div>

            <div>
              <h3 className="font-semibold mb-4">Compare</h3>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><a href="/compare/zendesk" className="hover:text-white">vs Zendesk</a></li>
                <li><a href="/compare/freshdesk" className="hover:text-white">vs Freshdesk</a></li>
                <li><a href="/compare/email" className="hover:text-white">vs Email</a></li>
                <li><a href="/pricing-comparison" className="hover:text-white">Pricing Comparison</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; 2025 TicketDesk. Compare customer support software pricing. Save thousands with no per-agent fees. Unlimited team members included.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PricingComparison;
